import { useState, useCallback } from 'react'
import { Cross2Icon, DownloadIcon } from '@radix-ui/react-icons'
import {
  ColumnFiltersState,
  OnChangeFn,
  RowData,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CommonDataTablePagination } from '@/components/ui/table/data-table-pagination'
import { DataTableToolbar } from '@/features/forms-and-surveys/components/data-table-toolbar'
import { useFnsColumns } from '@/features/forms-and-surveys/components/fns-columns'
import { useFnsMode } from '@/features/forms-and-surveys/hooks'
import { useExcelExport } from '@/features/forms-and-surveys/hooks/use-excel-export'
import { Survey } from '@/features/forms-and-surveys/types'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className: string
  }
}

interface FnsTableProps {
  data: Survey[]
  total: number
  loading: boolean
  refetch: () => Promise<unknown>
  refetching: boolean
  pagination: {
    pageIndex: number
    pageSize: number
  }
  setPagination: OnChangeFn<{ pageIndex: number; pageSize: number }>
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}

export function FnsTable({
  data,
  total,
  loading,
  refetch,
  refetching,
  pagination,
  setPagination,
  columnFilters,
  setColumnFilters,
}: FnsTableProps) {
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isExportingAll, setIsExportingAll] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const { t } = useTranslation()
  const mode = useFnsMode()
  const {
    exportSurveysToExcel,
    exportFormsToExcel,
    exportAllSurveysToExcel,
    exportAllFormsToExcel,
  } = useExcelExport()

  const columns = useFnsColumns()

  const table = useReactTable({
    data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),

    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: total,
    onPaginationChange: setPagination,

    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,

    state: {
      pagination,
      columnFilters,
      columnVisibility,
      rowSelection,
    },

    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  const isFiltered = table.getState().columnFilters.length > 0

  // Handle export to Excel
  const handleExportToExcel = useCallback(() => {
    const selectedRows = table.getFilteredSelectedRowModel().rows
    const itemsToExport =
      selectedRows.length > 0 ? selectedRows.map((row) => row.original) : data

    const filename =
      selectedRows.length > 0
        ? `${mode === 'survey' ? 'surveys' : 'forms'}-selected-export`
        : `${mode === 'survey' ? 'surveys' : 'forms'}-current-page-export`

    if (mode === 'survey') {
      exportSurveysToExcel(itemsToExport, { filename })
    } else {
      exportFormsToExcel(itemsToExport, { filename })
    }
  }, [data, table, mode, exportSurveysToExcel, exportFormsToExcel])

  // Handle export all items with pagination
  const handleExportAll = useCallback(async () => {
    setIsExportingAll(true)
    setExportProgress(0)

    try {
      let exportedCount = 0
      if (mode === 'survey') {
        exportedCount = await exportAllSurveysToExcel({
          filename: 'surveys-all-export',
          onProgress: (current, estimated) => {
            setExportProgress(
              Math.min(Math.round((current / estimated) * 100), 99)
            )
          },
        })
      } else {
        exportedCount = await exportAllFormsToExcel({
          filename: 'forms-all-export',
          onProgress: (current, estimated) => {
            setExportProgress(
              Math.min(Math.round((current / estimated) * 100), 99)
            )
          },
        })
      }

      setExportProgress(100)
    } catch (error) {
      console.error(`Failed to export all ${mode}s:`, error)
    } finally {
      setIsExportingAll(false)
      setTimeout(() => setExportProgress(0), 2000)
    }
  }, [mode, exportAllSurveysToExcel, exportAllFormsToExcel])

  const handleFiltersChange: OnChangeFn<ColumnFiltersState> = (filters) => {
    setColumnFilters(filters)
    table.resetPagination()
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 items-center space-x-2'>
          <Input
            placeholder={
              mode === 'survey'
                ? t('surveys.filterResponses')
                : t('forms.filterForms')
            }
            value={(table.getColumn('title')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('title')?.setFilterValue(event.target.value)
            }
            className='h-8 w-[150px] lg:w-[250px]'
          />
          {isFiltered && (
            <Button
              variant='ghost'
              onClick={() => table.resetColumnFilters()}
              className='h-8 px-2 lg:px-3'
            >
              {t('common.reset')}
              <Cross2Icon className='h-4 w-4' />
            </Button>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          {table.getFilteredSelectedRowModel().rows.length > 0 && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleExportToExcel}
              disabled={loading || refetching}
              className='h-8'
            >
              <DownloadIcon className='mr-2 h-4 w-4' />
              {table.getFilteredSelectedRowModel().rows.length > 0
                ? t(
                    mode === 'survey'
                      ? 'surveys.exportSelected'
                      : 'forms.exportSelected',
                    {
                      count: table.getFilteredSelectedRowModel().rows.length,
                    }
                  )
                : t(
                    mode === 'survey'
                      ? 'surveys.exportCurrentPage'
                      : 'forms.exportCurrentPage'
                  )}
            </Button>
          )}
          <Button
            variant='default'
            size='sm'
            onClick={handleExportAll}
            disabled={isExportingAll || loading || refetching}
            className='h-8'
          >
            <DownloadIcon className='mr-2 h-4 w-4' />
            {isExportingAll
              ? `${t(mode === 'survey' ? 'surveys.exporting' : 'forms.exporting')}... ${exportProgress}%`
              : t(mode === 'survey' ? 'surveys.exportAll' : 'forms.exportAll')}
          </Button>
          <DataTableToolbar
            table={table}
            refetch={refetch}
            refetching={refetching}
          />
        </div>
      </div>
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className='group/row'>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={header.column.columnDef.meta?.className ?? ''}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading || refetching ? (
              // Skeleton loading state
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} className='group/row'>
                  {columns.map((column, columnIndex) => (
                    <TableCell
                      key={`skeleton-cell-${columnIndex}`}
                      className={column.meta?.className ?? ''}
                    >
                      <Skeleton className='h-4 w-full' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='group/row'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className ?? ''}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {t('surveys.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <CommonDataTablePagination<Survey> table={table} />
    </div>
  )
}
